import {
  AssignmentStatusEnum,
  RouteGapSuggestionsDTO,
  RoutePlanFeedbackChangeTagEnum,
  RouteRebookingKindEnum,
  RouteRebookingProposalDTO,
} from 'api/model';
import Routes from 'constants/Routes';
import type { GapChoice } from '../Ruttplanering/selection-helpers';
import { MAX_RATING, MIN_RATING } from '../rating';

// Kvällens ruttprov — se .claude/features/route-planning.md, "Kvällens ruttprov".

export const FEEDBACK_QUESTION =
  'Tycker du att detta är ett bra rutt-förslag av ruttplaneringen?';

export const FEEDBACK_ONLY_NOTE =
  'Obs: detta är bara för feedback – du kommer inte att planera förslaget.';

/** Klienten frågar servern högst en gång i minuten; servern avgör om det blir en toast. */
export const PROMPT_POLL_INTERVAL_MS = 60_000;

/** Hur ofta grinden prövas. Kort, så att frågan kan ställas strax efter att det blivit tyst. */
export const PROMPT_GATE_CHECK_MS = 2_000;

/** Bara den som klickat, skrivit eller scrollat de senaste fem minuterna räknas som aktiv. */
export const ACTIVE_WINDOW_MS = 5 * 60_000;

/**
 * Toasten avbryter aldrig någon mitt i arbetet: den frågas efter, och visas, först när det varit
 * tyst (inga klick, tangenter eller scroll) så här länge.
 */
export const QUIET_BEFORE_PROMPT_MS = 10_000;

/** Hur länge toasten står kvar om ingen rör den. */
export const PROMPT_TOAST_DURATION_MS = 60_000;

/**
 * Ändringstaggarna visas när betyget är 4 eller lägre: vid 5 fanns inget att ändra
 * (beslut 2026-10-18, betyget 1–5 ersatte de tre svaren).
 */
export const showsChangeTags = (rating: number | undefined): boolean =>
  rating !== undefined && rating < MAX_RATING;

/** Följer regelområdena i route-planning.md så att svaren går att sortera mot planeraren. */
export const CHANGE_TAG_OPTIONS: {
  value: RoutePlanFeedbackChangeTagEnum;
  label: string;
}[] = [
  {
    value: RoutePlanFeedbackChangeTagEnum.OtherFieldTester,
    label: 'Annan fälttestare',
  },
  { value: RoutePlanFeedbackChangeTagEnum.LegOrder, label: 'Ordningen' },
  { value: RoutePlanFeedbackChangeTagEnum.DayTooLong, label: 'För lång dag' },
  { value: RoutePlanFeedbackChangeTagEnum.DayTooShort, label: 'För kort dag' },
  {
    value: RoutePlanFeedbackChangeTagEnum.Transfer,
    label: 'Orimlig överföring',
  },
  {
    value: RoutePlanFeedbackChangeTagEnum.TimesDoNotHold,
    label: 'Tiderna håller inte',
  },
  {
    value: RoutePlanFeedbackChangeTagEnum.ShouldBeManual,
    label: 'Borde planeras manuellt',
  },
  {
    value: RoutePlanFeedbackChangeTagEnum.GapSolution,
    label: 'Hotell, tåg eller annons',
  },
  { value: RoutePlanFeedbackChangeTagEnum.Other, label: 'Annat' },
];

export const changeTagLabel = (tag: RoutePlanFeedbackChangeTagEnum): string =>
  CHANGE_TAG_OPTIONS.find((o) => o.value === tag)?.label ?? 'Annat';

/** "2026-10-13" → "tisdag 13 oktober". Tolkas som kalenderdatum, utan tidszon. */
export const formatPlanningDate = (planningDate: string): string => {
  const [year, month, day] = planningDate.split('-').map(Number);
  if (!year || !month || !day) return planningDate;
  return new Date(year, month - 1, day).toLocaleDateString('sv-SE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
};

export const formatClock = (value: Date | string | null | undefined): string =>
  value
    ? new Date(value).toLocaleTimeString('sv-SE', {
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';

/**
 * Förklaringen till ett överspelat paket: något av benen har planerats sedan provet, så förslaget
 * visar ett läge som inte finns längre (ruttprovet 2026-10-25).
 */
export const describeOutdated = (
  generatedUtc: Date | string,
  outdatedSinceUtc: Date | string | null | undefined
): string => {
  const first = outdatedSinceUtc
    ? ` (det första ${formatClock(outdatedSinceUtc)})`
    : '';
  return `Minst ett av uppdragen har planerats, låsts eller avslutats sedan förslaget togs fram ${formatClock(generatedUtc)}${first}, så det visar ett läge som inte finns längre. Du kan ändå svara, men bedöm då förslaget som det såg ut när det togs fram.`;
};

/** Sidor där en toast vore i vägen: själva feedbacksidan och planeringsvyn. */
const QUIET_PATHS = [
  Routes.transportLogistics.routePlanFeedback,
  Routes.transportLogistics.routePlanning,
];

export interface ShowGate {
  visible: boolean;
  now: number;
  lastActivity: number;
  pathname: string;
  modalOpen: boolean;
}

export interface PollGate extends ShowGate {
  lastPoll: number;
  toastShowing: boolean;
}

const isQuiet = (gate: ShowGate): boolean =>
  gate.now - gate.lastActivity >= QUIET_BEFORE_PROMPT_MS;

/**
 * Får en toast dyka upp just nu? Bara i en synlig flik, efter tio sekunders tystnad, utan öppen
 * modal och inte på sidorna där den vore i vägen. Prövas igen precis innan en hämtad fråga visas.
 */
export const canShowPrompt = (gate: ShowGate): boolean =>
  gate.visible &&
  isQuiet(gate) &&
  !gate.modalOpen &&
  !QUIET_PATHS.some((path) => gate.pathname.startsWith(path));

/**
 * Ska klienten fråga servern om en toast nu? Varje minut för den som jobbat de senaste fem
 * minuterna, i en synlig flik, och aldrig när en toast redan visas eller väntar.
 *
 * Tystnaden och modalen prövas INTE här, bara när frågan ska visas (canShowPrompt). Serverns
 * lotteri räknar med en poll i minuten genom hela rundan — hade pollen också krävt tio sekunders
 * tystnad hade den som jobbar oavbrutet pollat sällan och nästan aldrig blivit tillfrågad.
 */
export const shouldPollForPrompt = (gate: PollGate): boolean =>
  gate.visible &&
  gate.now - gate.lastActivity <= ACTIVE_WINDOW_MS &&
  gate.now - gate.lastPoll >= PROMPT_POLL_INTERVAL_MS &&
  !gate.toastShowing &&
  !QUIET_PATHS.some((path) => gate.pathname.startsWith(path));

const UNPLANNED_STATUSES: AssignmentStatusEnum[] = [
  AssignmentStatusEnum.Created,
  AssignmentStatusEnum.OnHold,
];

export const isUnplanned = (status: AssignmentStatusEnum): boolean =>
  UNPLANNED_STATUSES.includes(status);

export const buildFeedbackPageUrl = (
  planningDate?: string,
  packageIndex?: number,
  rating?: number,
  // Ett visst av dagens prov (dagen kan ha flera: 14:00, 17:00, "Kör nu"). Utan: det senaste.
  runId?: number
): string => {
  const params = new URLSearchParams();
  if (planningDate) params.set('datum', planningDate);
  if (packageIndex !== undefined) params.set('paket', String(packageIndex));
  if (rating !== undefined) params.set('betyg', String(rating));
  if (runId !== undefined) params.set('prov', String(runId));
  const query = params.toString();
  return `${Routes.transportLogistics.routePlanFeedback}${query ? `?${query}` : ''}`;
};

export const parseRating = (value: string | null): number | undefined => {
  if (value === null || value === '') return undefined;
  const parsed = Number(value);
  return Number.isInteger(parsed) &&
    parsed >= MIN_RATING &&
    parsed <= MAX_RATING
    ? parsed
    : undefined;
};

/** Hur länge tacket står innan nästa förslag glider in. */
export const THANKS_BEFORE_NEXT_MS = 1_100;

export interface FeedbackNavigationItem {
  packageIndex: number;
  answered: boolean;
}

/**
 * Nästa obesvarade paket efter `current` i provets bläddringslista, runt till början om det
 * behövs. `current` räknas aldrig, så det paket man just svarat på eller hoppar över kommer inte
 * tillbaka direkt. Undefined när allt annat är besvarat.
 */
export const nextUnanswered = (
  navigation: FeedbackNavigationItem[],
  current: number
): number | undefined => {
  const at = navigation.findIndex((n) => n.packageIndex === current);
  for (let step = 1; step <= navigation.length; step += 1) {
    const item = navigation[(at + step) % navigation.length];
    if (item.packageIndex !== current && !item.answered) {
      return item.packageIndex;
    }
  }
  return undefined;
};

const formatTb = (tbSek: number | null | undefined): string =>
  tbSek == null
    ? 'TB okänt'
    : `TB ${tbSek > 0 ? '+' : tbSek < 0 ? '−' : ''}${Math.abs(Math.round(tbSek)).toLocaleString('sv-SE')} kr`;

/**
 * Vad planeringsvyn hade förvalt för en lucka, i klartext. Feedbackvyn visade förut bara tåget och
 * "behöver tåg, annons eller utfyllnad", så ett paket där planeraren förvalde en mellanlandad bil
 * bedömdes som "3 timmars tågresa" (ruttprovet 2026-10-21, paket #42). Valet är detsamma som i
 * planeringsvyn (`defaultSelection`), så trafikledaren bedömer det förslag som faktiskt hade
 * verkställts. Undefined = inget val att visa (gör inget på en vanlig lucka).
 */
export const describePlannedRemedy = (
  choice: GapChoice,
  gap: RouteGapSuggestionsDTO | null | undefined
): string | undefined => {
  if (!gap) return undefined;
  switch (choice) {
    case 'personalTransport':
      return gap.personalTransport
        ? `Planerat: kollektivt${gap.personalTransport.description ? ` · ${gap.personalTransport.description}` : ''} · ${Math.round(gap.personalTransport.durationSeconds / 60)} min`
        : undefined;
    case 'assignmentRequest':
      return gap.assignmentRequest
        ? `Planerat: annonsbil ${gap.assignmentRequest.fromCompanyName} → ${gap.assignmentRequest.toCompanyName} · ${formatTb(gap.assignmentRequest.tbSek)}`
        : undefined;
    case 'filler': {
      const { filler } = gap;
      if (!filler || filler.availableCount === 0) return undefined;
      const kind = filler.topCandidateIsStopover ? 'mellanlandad bil' : 'oplanerat uppdrag';
      const reg = filler.topCandidateRegistrationNumber ? ` ${filler.topCandidateRegistrationNumber}` : '';
      const route =
        filler.topCandidateFromCity && filler.topCandidateToCity
          ? ` ${filler.topCandidateFromCity} → ${filler.topCandidateToCity}`
          : '';
      return `Planerat: bil åt rätt håll (${kind}${reg}${route}) · ${formatTb(filler.topCandidateTbSek)}`;
    }
    case 'hubStopover':
      return gap.hubStopover ? `Planerat: mellanlandning vid ${gap.hubStopover.hubName}` : undefined;
    case 'rideAlong':
      return gap.rideAlong
        ? `Planerat: samåkning med ${gap.rideAlong.carrierFieldTesterName}`
        : undefined;
    default:
      return gap.isIdleWaitOnly && gap.idleWaitMinutes != null
        ? `Väntar ${gap.idleWaitMinutes} min till nästa uppdrag`
        : undefined;
  }
};

const hours = (value: number): string => value.toFixed(1).replace('.', ',');

/** En flytt i klartext: "WCC08R 08:30 → 09:30 (+60 min), ring kunden". */
export const describeRebookingMove = (r: RouteRebookingProposalDTO): string =>
  `${r.registrationNumber ?? `Uppdrag ${r.assignmentId}`} ${formatClock(r.currentBookedTo)} → ${formatClock(r.proposedBookedTo)} (${r.shiftMinutes > 0 ? '+' : '−'}${Math.abs(r.shiftMinutes)} min), ring ${r.callCustomer ? 'kunden' : 'bokaren'}`;

/**
 * Vad paketets ombokningsförslag ger, i klartext — så att trafikledaren betygsätter förslaget
 * inklusive tidsoptimeringen (Jonas 2026-10-22, efter ruttprovets #28: förslaget kortade 244 min
 * väntan till 124, men feedbackvyn visade det inte). Förslag som hör ihop (två flyttar kring samma
 * håltimme) beskriver samma dag och väntan, så effekten står en gång. Undefined utan förslag.
 */
export const describeRebookingEffect = (
  proposals: RouteRebookingProposalDTO[] | null | undefined
): string | undefined => {
  if (!proposals?.length) return undefined;
  // En mening per sort: en ifylld dag och en kortad håltimme är två olika saker att säga ja till.
  const kinds = Array.from(new Set(proposals.map((r) => r.kind)));
  return kinds
    .map((kind) => {
      const ofKind = proposals.filter((r) => r.kind === kind);
      const first = ofKind[0];
      const moves = ofKind.length > 1 ? 'flyttarna' : 'flytten';
      const day = `dagen ${hours(first.dayHoursBefore)} → ${hours(first.dayHoursAfter)} h`;
      return kind === RouteRebookingKindEnum.ShortenWait &&
        first.waitMinutesBefore != null &&
        first.waitMinutesAfter != null
        ? `Med ${moves}: väntan ${first.waitMinutesBefore} → ${first.waitMinutesAfter} min, ${day}. Paketet går också att köra som det är.`
        : `Med ${moves}: ${day}. Paketet går ihop först när kunden sagt ja.`;
    })
    .join(' ');
};
